import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { GroupTag } from '../../../../../state/ducks/auth/types';
import { companyActions, companySelectors } from '../../../../../state/ducks/company';
import { Company } from '../../../../../state/ducks/company/types';
import { AsyncStatus } from '../../../../../state/types';
import PromptIfDirty from '../../../../components/forms/PromptIfDirty';
import { toastError } from '../../../../components/notifications';
import { HOME_URL } from '../../../../constants/urls';
import useActionCreator from '../../../../hooks/useActionCreator';
import useAsync from '../../../../hooks/useAsync';
import useGetHasTag from '../../../../hooks/useGetHasTag';
import CompanyInformationPresenter from './presenter';

const CompanyInformationContainer: React.FC = () => {
  const initialValues = useSelector(companySelectors.getCompanyMine);
  const updatedAt = useSelector(companySelectors.getCompanyMineUpdatedAt);
  const [doNotPrompt, setDoNotPrompt] = useState(false);
  const canManageSettings = useGetHasTag(GroupTag.ADMIN_MANAGEMENT);
  const changeCompanyAction = useActionCreator(companyActions.updateCompanyMine);
  const async = useAsync({
    onError: toastError,
  });
  const isLoading = async.asyncState.status === AsyncStatus.Active;

  const formik = useFormik<Company>({
    initialValues,
    onSubmit: (values: Company) => {
      async.start(changeCompanyAction, { addresses: values.addresses, name: values.name }, async);
    },
  });

  const { dirty, setValues } = formik;

  useEffect(() => {
    if (!isLoading) {
      setValues(initialValues);
    }
  }, [setValues, initialValues, isLoading]);

  useEffect(() => {
    if (async.asyncState.status === AsyncStatus.Error && async.asyncState.message) {
      toastError(async.asyncState.message);
    }
  }, [async.asyncState]);

  const handlePromptConfirm = () => setDoNotPrompt(true);

  if (!canManageSettings) {
    return <Redirect to={HOME_URL} />;
  }

  return (
    <FormikProvider value={formik}>
      <PromptIfDirty
        isDialog
        dirty={dirty}
        doNotPrompt={doNotPrompt}
        onConfirm={handlePromptConfirm}
      />
      <CompanyInformationPresenter
        onSave={formik.submitForm}
        updatedAt={updatedAt}
        isLoading={isLoading}
      />
    </FormikProvider>
  );
};

export default CompanyInformationContainer;
